import React from 'react';
import {Box, Checkbox, FormControlLabel, TextField, Typography, Switch, Stack} from '@mui/material';
import MdEditor from "crqlar-react-common/dist/components/MdEditor";
import MdRenderer from "crqlar-react-common/dist/components/MdRenderer";
import {LegalQuestion} from "crqlar-react-common/dist/models/forms/form";
import {useTranslation} from "react-i18next";

interface LegalInputProps {
  question: LegalQuestion;
  onQuestionUpdate: (question: LegalQuestion) => void;
  isSelected?: boolean;
  onSelect?: () => void;
}

const LegalInput: React.FC<LegalInputProps> = ({
  question,
  onQuestionUpdate,
  isSelected = false,
  onSelect
}) => {
  const {t} = useTranslation();

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionUpdate({
      ...question,
      title: e.target.value
    });
  };

  const handleDescriptionChange = (value?: string) => {
    onQuestionUpdate({
      ...question,
      description: value || ''
    });
  };

  const handleTermsChange = (value?: string) => {
    onQuestionUpdate({
      ...question,
      termsText: value || ''
    });
  };

  const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionUpdate({
      ...question,
      required: e.target.checked
    });
  };

  const handleRequireSignatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionUpdate({
      ...question,
      requireSignature: e.target.checked
    });
  };

  return (
    <Stack
      onClick={onSelect}
      sx={{
        p: 3,
        mb: 2,
        borderRadius: 1,
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        backgroundColor: isSelected ? 'action.selected' : 'background.paper',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: 'primary.light',
          backgroundColor: 'action.hover'
        },
        position: 'relative'
      }}
    >
      <Box sx={{alignSelf: 'flex-end', display: 'flex', flexDirection: 'column', gap: 0.5}}>
        <FormControlLabel
          control={
            <Switch
              checked={question.required || false}
              onChange={handleRequiredChange}
              onClick={(e) => e.stopPropagation()}
              size="small"
            />
          }
          label={<Typography variant="caption">{t('Required')}</Typography>}
          labelPlacement="start"
          onClick={(e) => e.stopPropagation()}
          sx={{m: 0}}
        />
        <FormControlLabel
          control={
            <Switch
              checked={question.requireSignature || false}
              onChange={handleRequireSignatureChange}
              onClick={(e) => e.stopPropagation()}
              size="small"
            />
          }
          label={<Typography variant="caption">{t('Require Signature')}</Typography>}
          labelPlacement="start"
          onClick={(e) => e.stopPropagation()}
          sx={{m: 0}}
        />
      </Box>
      {isSelected ? (
        <>
          <TextField
            fullWidth
            label={t('Agreement Label')}
            value={question.title}
            onChange={handleTitleChange}
            variant="standard"
            sx={{mb: 2, pr: 12}}
            onClick={(e) => e.stopPropagation()}
            placeholder={t('e.g., I agree to the terms and conditions')}
          />

          <Box sx={{mb: 2}} onClick={(e) => e.stopPropagation()}>
            <MdEditor
              value={question.description || ''}
              onChange={handleDescriptionChange}
              placeholder={t('Question Description (optional)')}
              className={'mini-mdx'}
            />
          </Box>

          <Box sx={{mb: 2}} onClick={(e) => e.stopPropagation()}>
            <MdEditor
              value={question.termsText || ''}
              onChange={handleTermsChange}
              placeholder={t('Enter the legal terms, privacy policy, or agreement text...')}
              className={'mini-mdx'}
            />
          </Box>
        </>
      ) : (
        <>
          {question.description && (
            <Box sx={{mb: 2}}>
              <MdRenderer>
                {question.description}
              </MdRenderer>
            </Box>
          )}

          {question.termsText && (
            <Box
              sx={{
                p: 2,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                maxHeight: 150,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                mb: 2
              }}
            >
              <MdRenderer>
                {question.termsText}
              </MdRenderer>
            </Box>
          )}
        </>
      )}

      <FormControlLabel
        control={<Checkbox disabled />}
        label={
          <Typography variant="body2">
            {t('I accept')}
          </Typography>
        }
      />

    </Stack>
  );
};

export default LegalInput;
